/**
 * NewsBell — clickable bell icon shown in the client header.
 * Polls /api/news/unread-count every 30s; shows a red animated badge with
 * the count. Clicking opens a side drawer that lists every published news
 * item; opening any item marks it as read (POST /api/news/{id}/view).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Bell, X, Loader2, Calendar, ChevronRight, Newspaper } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import SafeHtml from './SafeHtml';
import { subscribeUnreadRefresh, triggerUnreadRefresh } from '../lib/unreadBus';
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewsBell({ token, unreadCount, managed = false, className = '' }) {
  const { t } = useTranslation('common');
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null); // currently expanded item
  const [loading, setLoading] = useState(false);
  // Parent-owned count (unified /api/badges/summary) wins; the local
  // poll only runs when the bell is used standalone.
  const polled = !managed && (unreadCount === undefined || unreadCount === null);

  const loadUnread = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/news/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return;
      const d = await r.json();
      setUnread(Number(d.unread || 0));
    } catch (_) { /* silent */ }
  }, [token]);

  useEffect(() => {
    if (!polled) { setUnread(Number(unreadCount || 0)); return undefined; }
    loadUnread();
    const id = setInterval(loadUnread, 120000);
    const unsub = subscribeUnreadRefresh(loadUnread);
    return () => { clearInterval(id); unsub(); };
  }, [loadUnread, polled, unreadCount]);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/news/feed`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) { setItems([]); return; }
      setItems(await r.json());
    } catch (_) { setItems([]); }
    finally { setLoading(false); }
  }, [token]);

  const openDrawer = () => { setOpen(true); loadFeed(); };
  const closeDrawer = () => { setOpen(false); setActive(null); };

  const markRead = async (item) => {
    if (item.is_read) { setActive(item); return; }
    try {
      await fetch(`${API_URL}/api/news/${item.id}/view`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      // Optimistic update
      setItems((prev) => prev.map((i) => i.id === item.id ? { ...i, is_read: true } : i));
      setUnread((u) => Math.max(0, u - 1));
      triggerUnreadRefresh();
    } catch (_) { /* silent */ }
    setActive(item);
  };

  const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  return (
    <>
      <button
        type="button"
        onClick={openDrawer}
        data-testid="news-bell-toggle"
        title={unread > 0 ? `${unread} unread news` : 'News'}
        className={`relative w-10 h-10 rounded-full flex items-center justify-center bg-white/15 hover:bg-white/25 transition-colors ${className}`}
      >
        <Bell className="w-5 h-5 text-white" />
        {unread > 0 && (
          <>
            <span data-testid="news-bell-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white rounded-full text-[10px] font-extrabold flex items-center justify-center">
              {unread > 99 ? '99+' : unread}
            </span>
            <span className="absolute -top-1 -right-1 w-[18px] h-[18px] rounded-full bg-red-400 animate-ping opacity-75" />
          </>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/70 backdrop-blur-sm overflow-y-auto p-4 sm:p-8"
          onClick={closeDrawer}
          data-testid="news-drawer"
        >
          <div
            className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl my-4 overflow-hidden flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-cyan-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <Newspaper className="w-6 h-6" />
                <h3 className="font-bold text-lg">{t('news.title', { defaultValue: 'News & Updates' })}</h3>
                {items.length > 0 && (
                  <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">{items.length} {t(items.length === 1 ? 'news.post_one' : 'news.post_other', { count: items.length, defaultValue: items.length === 1 ? 'post' : 'posts' })}</span>
                )}
              </div>
              <button onClick={closeDrawer} data-testid="news-drawer-close"
                className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"><X className="w-5 h-5" /></button>
            </div>

            {/* Body */}
            {active ? (
              // Expanded single news view — large readable typography
              <div className="flex-1 overflow-y-auto" data-testid="news-detail">
                <div className="max-w-3xl mx-auto px-6 sm:px-10 py-6 sm:py-8">
                  <button
                    onClick={() => setActive(null)}
                    className="text-sm text-cyan-700 hover:text-cyan-800 mb-4 flex items-center gap-1.5 font-medium"
                    data-testid="news-detail-back"
                  >
                    ← Back to all news
                  </button>
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 leading-tight">{active.title}</h1>
                  <p className="text-sm text-slate-500 flex items-center gap-1.5 mb-6 pb-4 border-b border-slate-100">
                    <Calendar className="w-4 h-4" /> {fmt(active.created_at)}
                  </p>
                  {/* Trusted admin-generated HTML from Tiptap. Self-contained
                      `news-body` class (defined in App.css) gives reliable
                      typography even when the Tailwind typography plugin isn't
                      installed. */}
                  <SafeHtml as="div" className="news-body" html={active.body_html} />
                </div>
              </div>
            ) : loading ? (
              <div className="flex-1 flex items-center justify-center text-slate-500 py-20">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading news…
              </div>
            ) : items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-400 px-6 py-20 text-center">
                <Newspaper className="w-14 h-14 mb-3 opacity-40" />
                <p className="text-base">No news at the moment. Check back later!</p>
              </div>
            ) : (
              // Card grid for the list — easier to scan than a sidebar list.
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {items.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => markRead(n)}
                      data-testid={`news-item-${n.id}`}
                      className={`group text-left rounded-xl border-2 p-4 sm:p-5 transition-all hover:shadow-md hover:-translate-y-0.5 ${
                        n.is_read
                          ? 'bg-white border-slate-200'
                          : 'bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200 ring-1 ring-cyan-100'
                      }`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        {!n.is_read && (
                          <span className="mt-1.5 flex-shrink-0 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
                        )}
                        <h4 className={`flex-1 ${n.is_read ? 'font-semibold text-slate-700' : 'font-bold text-slate-900'} text-base sm:text-lg leading-snug`}>
                          {n.title}
                        </h4>
                        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                      <p className="text-xs text-slate-500 flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" /> {fmt(n.created_at)}
                        {!n.is_read && (
                          <span className="ml-auto text-[10px] font-bold uppercase tracking-wider bg-red-500 text-white px-2 py-0.5 rounded-full">New</span>
                        )}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
